import { useCallback, useEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { IOS_APP_URL } from "@/constants/url"
import { CurrentUser, UserPermission } from "@/types/user"
import { cn } from "cn"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { APP_HOME_SEARCH_PILLS, appHomeSearchHref } from "@/lib/app-home-search"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { isEuRegion } from "@/lib/region"
import { useRouter } from "@/lib/router"
import { openAuthDialog } from "@/lib/storage/auth-thunks"
import { useAppDispatch } from "@/lib/storage/hooks"
import { getPageAdClickIds } from "@/lib/utils/geo"
import IconMenu from "@/components/icons/menu"
import Logo from "@/components/logo"
import { Button, ButtonProps } from "@/components/ui/button"
import { IconButton } from "@/components/ui/icon-button"
import { Link } from "@/components/ui/link"
import { MarketingEntryCtaButton } from "./marketing-entry-cta-button"
import { MarketingSheetNav } from "./sheet-nav"
import { MarketingUserDropdown } from "./user-dropdown"

export function MarketingHeader({
  initUser,
  knockout,
  isBusiness,
  permissions,
}: {
  initUser?: CurrentUser
  knockout?: boolean
  isBusiness?: boolean
  permissions?: UserPermission[]
}) {
  const dispatch = useAppDispatch()
  const router = useRouter()

  const t = useTranslation()
  const [navOpen, setNavOpen] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const isCreatorProgram = pathname === "/creator-program"
  const isAnonymous = !initUser || initUser.provider === "anonymous"

  const onScroll = useCallback(() => {
    const shouldBeScrolled = window.scrollY > 0
    if (isScrolled !== shouldBeScrolled) {
      setIsScrolled(shouldBeScrolled)
    }
  }, [isScrolled])

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <>
      {/* iOS 26 Safari paints a solid strip over the status bar when a fixed
          element touches the top edge. The 12px gap on phones keeps it off
          the edge so the page scrolls under the status bar. */}
      <div
        ref={headerRef}
        className={cn(
          "fixed inset-x-0 top-[calc(var(--banner-height,0px)+--spacing(3))] z-2 flex h-14 items-center rounded-full px-(--container-pad) text-foreground transition-colors duration-500 sm:top-(--banner-height,0px) sm:h-16 sm:px-7",
          knockout && "text-background [&_.group]:duration-0",
          isScrolled && !knockout && "text-foreground"
        )}
      >
        <div
          className={cn(
            "absolute inset-x-1 inset-y-0 rounded-full bg-background/80 backdrop-blur-md transition-opacity duration-500 sm:inset-x-3.5 sm:inset-y-2 sm:rounded-full",
            !isScrolled && "opacity-0"
          )}
        ></div>
        <div className="relative flex items-center md:basis-1/2">
          <IconButton
            variant="ghost"
            size="md"
            className="mr-0.5 -ml-2.5 sm:mr-2.5"
            icon={IconMenu}
            label={t("menu")}
            onClick={() => setNavOpen(true)}
          />

          <Link
            href={
              isBusiness
                ? "/business"
                : isCreatorProgram
                  ? "/creator-program"
                  : "/welcome"
            }
            className="flex shrink-0 items-center gap-2 leading-tight transition-opacity hover:opacity-60"
            aria-label={t("mindtripHomepage")}
          >
            <Logo size={28} />
            {(isBusiness || isCreatorProgram) && (
              <span className="mt-0.5 text-lg leading-none tracking-tight italic">
                <span className="font-serif lowercase">for</span>{" "}
                <span>{isBusiness ? "business" : "creators"}</span>
              </span>
            )}
          </Link>
        </div>

        {!isEuRegion() && !isBusiness && !isCreatorProgram && (
          <div className="hidden shrink-0 justify-center gap-2.5 lg:flex">
            {APP_HOME_SEARCH_PILLS.map((pill) => {
              const isActive =
                (pill.type === "flights" || pill.type === "stays") &&
                pathname === `/${pill.type}`

              return (
                <Button
                  key={pill.type}
                  variant="ghost"
                  size="sm"
                  {...(isActive
                    ? ({
                        className:
                          "pointer-events-none relative after:absolute after:-bottom-0.5 after:left-1/2 after:h-0.5 after:w-4 after:-translate-x-1/2 after:rounded-full after:bg-current",
                        "aria-current": "page",
                      } satisfies ButtonProps)
                    : { href: appHomeSearchHref(pill.type) })}
                >
                  {pill.label}
                </Button>
              )
            })}
          </div>
        )}

        <div className="ml-auto flex min-w-0 justify-end pl-3 md:basis-1/2">
          <div
            className={cn(
              "-mr-2 flex min-w-0 items-center sm:gap-2",
              !isAnonymous && "sm:mr-0"
            )}
          >
            {!isBusiness && !isCreatorProgram && (
              <Button
                variant="subtle"
                size="xs"
                href={IOS_APP_URL}
                onClick={() => {
                  const pathName = router.asPath
                  const clickId = getPageAdClickIds(pathName)

                  sendAnalyticsEvent({
                    name: "ClickedAppStoreButton",
                    properties: { clickId: clickId },
                  })
                }}
                className="lg:max-xl:hidden"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="13"
                  viewBox="0 0 9 13"
                  fill="none"
                >
                  <rect
                    x="0.4"
                    y="0.4"
                    width="8.2"
                    height="12.2"
                    rx="0.6"
                    stroke="black"
                    strokeWidth="0.8"
                  />
                  <path
                    d="M5 2H4"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                  />
                  <path
                    d="M3 11H6"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="ml-0.5">Get app</span>
              </Button>
            )}

            {isAnonymous && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() =>
                  void dispatch(
                    openAuthDialog({
                      callbackUrl: "/",
                    })
                  )
                }
              >
                Log in
              </Button>
            )}

            {!isAnonymous && initUser && (
              <div className="flex min-w-0">
                <MarketingUserDropdown
                  user={initUser}
                  permissions={permissions}
                />
              </div>
            )}
            <div className="hidden sm:contents">
              {isBusiness ? (
                <Button
                  variant="outline-clear"
                  size="xs"
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById("contact")?.scrollIntoView({
                      behavior: "smooth",
                    })
                  }}
                >
                  Book a demo
                </Button>
              ) : (
                <MarketingEntryCtaButton
                  variant="outline-clear"
                  size="xs"
                  rel="nofollow"
                  initialProvider={initUser?.provider}
                  setupLabel={t("marketingCtaSetupShort")}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <MarketingSheetNav
        open={navOpen}
        setOpen={setNavOpen}
        isAnonymous={isAnonymous}
      />
    </>
  )
}
